import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  Brush,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  TooltipContentProps,
  XAxis,
  YAxis,
} from "recharts";
import { WorkerQueryTransferModes } from "../../libs/workers/consts";
import { WorkerQueryOptions } from "../../libs/workers/types";
import { webWorkerQuery } from "../../libs/workers/webWorker";
import { LoadingGuard } from "../LoadingGuard";
import {
  AggregationFn,
  AggregationPeriod,
  BrushRange,
  ChartClickEvent,
  ChartSelection,
  ChartVendorProps,
  MetricConfig,
  RechartsChartData,
  RechartsDataPoint,
  RechartsSeriesConfig,
  TrendsOverChartEntry,
  YAxisVisualConfig,
} from "./TrendsOverTimeChart.types";
import TrendsOverTimeWorker from "./TrendsOverTimeChart.worker?worker";

// ============================================
// Data fetching helpers
// ============================================

async function getChartDataDual(
  entries: TrendsOverChartEntry[] | ArrayBufferLike | undefined | null,
  aggregationFns: { fn: AggregationFn; yAxisId: string }[],
  yAxisVisualConfigs: Record<string, YAxisVisualConfig>,
  metrics: MetricConfig[],
  aggregationPeriod: AggregationPeriod,
  opts?: WorkerQueryOptions
): Promise<RechartsChartData> {
  const worker = new TrendsOverTimeWorker();

  return webWorkerQuery(worker, {
    type: "PROCESS_CHART_DUAL",
    data: {
      entries,
      aggregationFns,
      yAxisVisualConfigs,
      metrics,
      aggregationPeriod,
    },
    opts: {
      traceKey: `TrendsOverTimeWorker-Dual-${Date.now()}`,
      ...opts,
      response: {
        transferMode: WorkerQueryTransferModes.SHARED_ARRAY_BUFFER,
        ...(opts?.response || {}),
      },
    },
  }).finally(() => {
    worker.terminate();
  });
}

/**
 * Simple hash for entries to detect data changes.
 * Uses length and sample of first/last entries for performance.
 */
function hashEntries(
  entries: TrendsOverChartEntry[] | ArrayBufferLike | undefined | null
): string {
  if (!entries) return "empty";
  if (entries instanceof SharedArrayBuffer || entries instanceof ArrayBuffer) {
    return `buffer-${entries.byteLength}`;
  }
  if (!Array.isArray(entries) || entries.length === 0) return "empty";
  // Use length + first/last timestamps for quick hash
  const first = entries[0];
  const last = entries[entries.length - 1];
  return `${entries.length}-${first?.timestamp}-${last?.timestamp}`;
}

/**
 * Hook to fetch and process chart data for Recharts dual Y-axis charts.
 * Entries should already have yAxisId set.
 */
function useChartDataDual(
  cacheKey: string,
  entries: TrendsOverChartEntry[] | ArrayBufferLike | undefined | null,
  aggregationFns: { fn: AggregationFn; yAxisId: string }[],
  yAxisVisualConfigs: Record<string, YAxisVisualConfig>,
  metrics: MetricConfig[],
  aggregationPeriod: AggregationPeriod,
  pending = false,
  opts?: WorkerQueryOptions
) {
  // Only pass enabled metrics to worker
  const enabledMetrics = useMemo(
    () => metrics.filter((m) => m.enabled),
    [metrics]
  );

  return useQuery({
    queryKey: [
      "TrendsOverTimeChartDataDual",
      cacheKey,
      hashEntries(entries),
      JSON.stringify(aggregationFns),
      JSON.stringify(yAxisVisualConfigs),
      JSON.stringify(enabledMetrics),
      aggregationPeriod,
    ],
    queryFn: () =>
      getChartDataDual(
        entries,
        aggregationFns,
        yAxisVisualConfigs,
        enabledMetrics,
        aggregationPeriod,
        opts
      ),
    staleTime: Infinity,
    gcTime: Infinity,
    enabled:
      !pending && enabledMetrics.length > 0 && hashEntries(entries) !== "empty",
  });
}

// ============================================
// Rendering helpers
// ============================================

const AGGREGATION_FN_LABELS: Record<AggregationFn, string> = {
  count: "Totale",
  average: "Media",
  sum: "Somma",
};

const buildTotalLabel = (keyLabel: string, fn: AggregationFn): string => {
  const fnLabel = AGGREGATION_FN_LABELS[fn];
  return `${fnLabel} ${keyLabel}`;
};

const toPercent = (decimal: number): string => `${(decimal * 100).toFixed(0)}%`;

const getPercent = (value: number, total: number): string => {
  const ratio = total > 0 ? value / total : 0;

  return toPercent(ratio);
};

/**
 * Format number to max 2 decimals, showing decimals only if they exist
 */
const formatValue = (value: number): string => {
  if (Number.isInteger(value)) return value.toString();
  // Use toFixed(2) then remove trailing zeros
  return parseFloat(value.toFixed(2)).toString();
};

/**
 * Helper to determine if percentage should be shown based on aggregation function
 */
const shouldShowPercentage = (fn: AggregationFn | undefined): boolean => {
  return fn === "sum" || fn === "count";
};

/**
 * Grouped metric structure for tooltip rendering
 */
interface GroupedMetric {
  baseName: string;        // "City Escape Bergamo - Colleoni"
  baseKey: string;         // "ce_bergamo_colleoni"
  entries: Array<{
    axis: 'left' | 'right';
    value: number;
    percentage: string;
    color: string;
    originalEntry: any;
  }>;
  leftValue: number;       // Value for left axis (used for sorting)
  totalValue: number;
  totalPercentage: string;
}

/**
 * Groups tooltip payload entries by base metric name,
 * combining left and right axis entries for the same metric.
 */
const groupByBaseMetric = (
  payload: Array<any>,
  leftTotal: number,
  rightTotal: number
): GroupedMetric[] => {
  const groups = new Map<string, GroupedMetric>();
  
  for (const entry of payload) {
    // Extract base name by removing " (comp.)"
    const baseName = entry.name.replace(/\s*\(comp\.\)\s*$/, '').trim();
    
    // Extract baseKey from dataKey by removing "comparand_" or "comparator_" prefix
    const baseKey = entry.dataKey.replace(/^(comparand_|comparator_)/, '');
    
    // Determine axis
    const axis = entry.dataKey.startsWith('comparand_') ? 'left' : 'right';
    const total = axis === 'left' ? leftTotal : rightTotal;
    
    if (!groups.has(baseKey)) {
      groups.set(baseKey, {
        baseName,
        baseKey,
        entries: [],
        leftValue: 0,
        totalValue: 0,
        totalPercentage: '0%'
      });
    }
    
    const group = groups.get(baseKey)!;
    const value = entry.value as number;
    
    group.entries.push({
      axis,
      value,
      percentage: getPercent(value, total),
      color: entry.color,
      originalEntry: entry
    });
    
    // Track left value for sorting
    if (axis === 'left') {
      group.leftValue = value;
    }
    
    group.totalValue += value;
  }
  
  // Calculate total percentage for each group (using combined left+right total)
  const grandTotal = leftTotal + rightTotal;
  groups.forEach(group => {
    group.totalPercentage = getPercent(group.totalValue, grandTotal);
  });
  
  // Convert to array and sort by leftValue (comparando) descending
  return Array.from(groups.values())
    .sort((a, b) => b.leftValue - a.leftValue);
};

/**
 * Creates a tooltip renderer with access to aggregation functions
 * via closure to determine when to show percentages.
 */
/**
 * Wrapper for value formatter that ensures proper decimal formatting
 */
const wrapValueFormatter = (
  formatter?: (value: number | null) => string,
  aggregationFn?: AggregationFn
) => {
  return (value: number | null): string => {
    if (value === null) return '-';
    
    // For count aggregation, never show decimals
    if (aggregationFn === 'count') {
      return Math.round(value).toString();
    }
    
    // If custom formatter exists, use it
    if (formatter) {
      return formatter(value);
    }
    
    // Default: show decimals only if they exist
    return formatValue(value);
  };
};

const createTooltipRenderer = (
  leftAggregationFn: AggregationFn,
  rightAggregationFn?: AggregationFn,
  leftKeyLabel?: string,
  rightKeyLabel?: string,
  leftValueFormatter?: (value: number | null) => string,
  rightValueFormatter?: (value: number | null) => string,
  chartData?: RechartsDataPoint[]
) => {
  // Build formatted labels like "Totale Avvii" or "Media Valutazione"
  const leftLabel = leftKeyLabel ? buildTotalLabel(leftKeyLabel, leftAggregationFn) : 'L';
  const rightLabel = rightKeyLabel && rightAggregationFn ? buildTotalLabel(rightKeyLabel, rightAggregationFn) : 'R';
  
  // Wrap formatters to ensure proper decimal handling
  const wrappedLeftFormatter = wrapValueFormatter(leftValueFormatter, leftAggregationFn);
  const wrappedRightFormatter = wrapValueFormatter(rightValueFormatter, rightAggregationFn);
  
  return (o: TooltipContentProps<number | string, string>) => {
    const { payload, label } = o;
    
    // Extract Totale entries for header (dataKey ends with _all)
    const totaleEntries = payload.filter(entry => 
      entry.dataKey?.endsWith('_all')
    );
    
    const leftTotaleEntry = totaleEntries.find(entry => entry.dataKey?.startsWith('comparand_'));
    const rightTotaleEntry = totaleEntries.find(entry => entry.dataKey?.startsWith('comparator_'));
    
    const leftTotal = (leftTotaleEntry?.value as number) ?? 0;
    const rightTotal = (rightTotaleEntry?.value as number) ?? 0;
    
    // Filter out "Totale" entries (those with _all in dataKey) for metrics display
    const metricsOnly = payload.filter(entry => 
      !entry.dataKey?.endsWith('_all')
    );

    // Separate left and right metrics
    const leftMetrics = metricsOnly.filter(entry => 
      entry.dataKey?.startsWith('comparand_')
    );
    
    const rightMetrics = metricsOnly.filter(entry => 
      entry.dataKey?.startsWith('comparator_')
    );

    // Group metrics by base name (use Totale line values for percentage calculation)
    const groupedMetrics = groupByBaseMetric(metricsOnly, leftTotal, rightTotal);
    
    // Enable compact mode for > 8 metrics
    const compactMode = groupedMetrics.length > 8;
    
    // Font sizes
    const headerFontSize = compactMode ? "0.65rem" : "0.75rem";
    const metricFontSize = compactMode ? "0.6rem" : "0.7rem";
    const valueFontSize = compactMode ? "0.58rem" : "0.65rem";
    
    // Spacing
    const rowGap = compactMode ? 0.1 : 0.25;
    const groupGap = compactMode ? 0.3 : 0.5;
    const columnGap = compactMode ? 1.5 : 2;
    const cardPadding = compactMode ? 1.5 : 2;
    
    // Calculate columns based on grouped metrics
    const columnCount = groupedMetrics.length <= 4 ? 1
      : groupedMetrics.length <= 8 ? 2
      : groupedMetrics.length <= 12 ? 3
      : 4;
    
    const rowsPerColumn = Math.ceil(groupedMetrics.length / columnCount);

    // Determine if we should show percentages for each axis
    const showLeftPercentage = shouldShowPercentage(leftAggregationFn);
    const showRightPercentage = shouldShowPercentage(rightAggregationFn);

    return (
      <Card sx={{ zIndex: 999999 }}>
        <CardContent sx={{ 
          maxWidth: compactMode ? 1400 : 1200, 
          padding: cardPadding,
          zIndex: 999 
        }}>
          {/* Header with totals from Totale entry */}
          <Box sx={{ 
            fontWeight: "bold", 
            pb: compactMode ? 0.3 : 0.5,
            mb: compactMode ? 0.3 : 0.5,
            borderBottom: 1, 
            borderColor: "divider",
            fontSize: headerFontSize,
            lineHeight: 1.2,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}>
            <Box component="span">{String(label || '')}</Box>
            {(() => {
              const totals: string[] = [];
              if (leftTotal > 0) {
                totals.push(`${leftLabel}: ${wrappedLeftFormatter(leftTotal)}`);
              }
              if (rightTotal > 0) {
                totals.push(`${rightLabel}: ${wrappedRightFormatter(rightTotal)}`);
              }
              if (totals.length > 0) {
                return (
                  <Box component="span" sx={{ color: 'text.secondary' }}>
                    ({totals.join(' | ')})
                  </Box>
                );
              }
              return null;
            })()}
          </Box>
          
          {/* Grid with vertical flow (top-down) */}
          <Box sx={{
            display: "grid",
            gridAutoFlow: "column",
            gridTemplateRows: `repeat(${rowsPerColumn}, auto)`,
            gridTemplateColumns: `repeat(${columnCount}, minmax(180px, 1fr))`,
            columnGap,
            rowGap: groupGap,
          }}>
            {groupedMetrics.map((group, idx) => (
              <Box
                key={group.baseKey}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: rowGap,
                  pl: idx >= rowsPerColumn ? 1.5 : 0,
                  borderLeft: idx >= rowsPerColumn ? 1 : 0,
                  borderColor: "divider",
                }}
              >
                {/* Base metric name */}
                <Typography 
                  sx={{ 
                    fontWeight: "bold",
                    fontSize: metricFontSize,
                    lineHeight: 1.3,
                    color: "text.primary",
                    mb: 0.2,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                  }}
                  title={group.baseName}
                >
                  {group.baseName}
                  {(() => {
                    // Extract count from metadata using __count_ prefix
                    const countEntry = group.entries.find(e => e.axis === 'left');
                    if (!countEntry) return null;
                    
                    const countKey = `__count_${countEntry.originalEntry.dataKey}`;
                    const count = countEntry.originalEntry.payload?.[countKey] as number | undefined;
                    
                    if (count && count > 0) {
                      return (
                        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 'normal', ml: 0.5 }}>
                          ({count})
                        </Box>
                      );
                    }
                    return null;
                  })()}
                </Typography>
                
                {/* Values on single line */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    pl: 1,
                  }}
                >
                  {group.entries.map((entry, entryIdx) => {
                    const showPercentage = entry.axis === 'left' 
                      ? showLeftPercentage 
                      : showRightPercentage;
                    
                    return (
                      <Box
                        key={`${group.baseKey}-${entry.axis}`}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.3,
                        }}
                      >
                        {/* Slash separator (only between entries) */}
                        {entryIdx > 0 && (
                          <Typography 
                            sx={{ 
                              fontSize: valueFontSize,
                              color: "text.disabled",
                              mx: 0.2,
                            }}
                          >
                            /
                          </Typography>
                        )}
                        
                        {/* Value with color */}
                        <Typography 
                          sx={{ 
                            fontSize: valueFontSize,
                            fontWeight: "bold",
                            color: entry.color,
                          }}
                        >
                          {entry.axis === 'left' ? wrappedLeftFormatter(entry.value) : wrappedRightFormatter(entry.value)}
                        </Typography>
                        
                        {/* Percentage (conditional on aggregation function) */}
                        {showPercentage && (
                          <Typography 
                            sx={{ 
                              fontSize: valueFontSize,
                              color: "text.secondary",
                              fontWeight: "medium",
                            }}
                          >
                            ({entry.percentage})
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  };
};

const renderSeries = (
  series: RechartsSeriesConfig[],
  aggregationMode: "compare" | "stack",
  connectNulls: boolean = false
) => {
  return series.map((s) => {
    const commonProps = {
      key: s.dataKey,
      dataKey: s.dataKey,
      name: s.label,
      stroke: s.color,
      fill: s.color,
      yAxisId: s.yAxisId,
      strokeDasharray: s.strokeDasharray,
      dot: false,
      activeDot: { r: 4 },
      connectNulls,
    };

    // Use Area for stacked mode, Line otherwise
    if (aggregationMode === "stack" && s.area !== false) {
      return (
        <Area
          {...commonProps}
          type="monotone"
          stackId={s.stackId ?? "default"}
          fillOpacity={0.6}
        />
      );
    }

    return <Line {...commonProps} type="monotone" />;
  });
};

// ============================================
// Event normalizers
// ============================================

const createClickHandler = (
  data: RechartsChartData["data"],
  onDataPointClick?: (event: ChartClickEvent) => void
) => {
  if (!onDataPointClick) return undefined;

  return (chartData: any) => {
    if (chartData?.activePayload?.[0]) {
      const payload = chartData.activePayload[0];
      const activeIndex = chartData.activeTooltipIndex ?? 0;
      const normalizedEvent: ChartClickEvent = {
        dataIndex: activeIndex,
        periodLabel: data[activeIndex]?.date ?? "",
        metricKey: payload.dataKey,
        value: payload.value,
        rawEvent: chartData,
      };
      onDataPointClick(normalizedEvent);
    }
  };
};

// ============================================
// Component
// ============================================

export function RechartsTrendsChart({
  cacheKey,
  entries,
  comparand,
  comparator,
  metrics,
  aggregationPeriod,
  aggregationMode,
  defaultAggregationFn,
  width = 800,
  height = 400,
  grid,
  loading = false,
  opts,
  onDataPointClick,
  onPeriodClick,
  brushEnabled = false,
  brushHeight = 40,
  brushRange: controlledBrushRange,
  onBrushChange,
  onChartDataReady,
  selectionEnabled = false,
  onSelectionChange,
  connectNulls = false,
}: ChartVendorProps) {
  const theme = useTheme();

  // Selection state for ReferenceArea drag-to-select
  const [refAreaLeft, setRefAreaLeft] = useState<string | null>(null);
  const [refAreaRight, setRefAreaRight] = useState<string | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);

  useEffect(() => {
    console.debug("RechartsTrendsChart comparation changed", {
      comparand,
      comparator,
    });
  }, [grid]);

  useEffect(() => {
    console.log('👀 Comparand/Comparator labels:', {
      comparandLabel: comparand.label,
      comparatorLabel: comparator?.label,
      comparandAggKey: comparand.aggregationKey
    });
  }, [comparand.label, comparator?.label, comparand.aggregationKey]);

  // Build aggregationFns array for the worker
  const aggregationFns = useMemo(() => {
    const fns: { fn: AggregationFn; yAxisId: string }[] = [
      {
        fn: comparand.aggregationFn ?? defaultAggregationFn,
        yAxisId: comparand.id,
      },
    ];
    if (comparator?.aggregationKey) {
      fns.push({
        fn: comparator.aggregationFn ?? defaultAggregationFn,
        yAxisId: comparator.id,
      });
    }
    return fns;
  }, [
    comparand.id,
    comparand.aggregationFn,
    comparator?.id,
    comparator?.aggregationFn,
    comparator?.aggregationKey,
    defaultAggregationFn,
  ]);

  // Build yAxisVisualConfigs for the worker
  const yAxisVisualConfigs = useMemo(() => {
    const configs: Record<string, YAxisVisualConfig> = {
      [comparand.id]: { yAxisId: "left" },
    };
    if (comparator?.aggregationKey && comparator.id) {
      configs[comparator.id] = {
        yAxisId: "right",
        labelSuffix: " (comp.)",
        strokeDasharray: "5 5",
      };
    }
    return configs;
  }, [comparand.id, comparator?.id, comparator?.aggregationKey]);

  // Single unified query for both trends
  const chartData = useChartDataDual(
    cacheKey,
    entries,
    aggregationFns,
    yAxisVisualConfigs,
    metrics,
    aggregationPeriod,
    loading,
    { ...opts, traceKey: opts?.traceKey ?? `RechartsTrendsChart-${cacheKey}` }
  );

  // Series come directly from worker with visual config applied
  const series = chartData.data?.series ?? [];

  // Determine if we have comparator data (series with yAxisId="right")
  const hasComparator = Boolean(
    comparator?.aggregationKey && series.some((s) => s.yAxisId === "right")
  );

  // Combined loading state
  const isLoading = loading || chartData.isLoading;

  // Y axis formatters
  const leftAxisFormatter = useMemo(
    () => comparand.yAxisConfig?.valueFormatter,
    [comparand.yAxisConfig]
  );

  const rightAxisFormatter = useMemo(
    () => comparator?.yAxisConfig?.valueFormatter,
    [comparator?.yAxisConfig]
  );

  // Create tooltip renderer with access to aggregation functions and labels
  const tooltipRenderer = useMemo(
    () => createTooltipRenderer(
      comparand.aggregationFn ?? defaultAggregationFn,
      comparator?.aggregationFn ?? defaultAggregationFn,
      comparand.label,
      comparator?.label,
      leftAxisFormatter,
      rightAxisFormatter,
      chartData.data?.data
    ),
    [comparand.aggregationFn, comparator?.aggregationFn, defaultAggregationFn, comparand.label, comparator?.label, leftAxisFormatter, rightAxisFormatter, chartData.data?.data]
  );

  // Brush change handler
  const handleBrushChange = useCallback(
    (range: BrushRange) => {
      onBrushChange?.(range);
    },
    [onBrushChange]
  );

  // ============================================
  // ReferenceArea selection handlers
  // ============================================

  /**
   * Find the index of a date label in the chart data.
   */
  const findDataIndex = useCallback(
    (label: string): number => {
      const data = chartData.data?.data ?? [];
      return data.findIndex((d) => d.date === label);
    },
    [chartData.data?.data]
  );

  /**
   * Build a ChartSelection from two date labels.
   * Ensures startIndex <= endIndex regardless of drag direction.
   */
  const buildSelection = useCallback(
    (left: string, right: string): ChartSelection | null => {
      const leftIndex = findDataIndex(left);
      const rightIndex = findDataIndex(right);

      if (leftIndex === -1 || rightIndex === -1) return null;

      // Normalize: ensure start <= end
      const [startIndex, endIndex] =
        leftIndex <= rightIndex
          ? [leftIndex, rightIndex]
          : [rightIndex, leftIndex];

      const data = chartData.data?.data ?? [];
      return {
        startLabel: data[startIndex]?.date ?? "",
        endLabel: data[endIndex]?.date ?? "",
        startIndex,
        endIndex,
      };
    },
    [findDataIndex, chartData.data?.data]
  );

  /**
   * Mouse down: start selection
   */
  const handleMouseDown = useCallback(
    (e: any) => {
      if (!selectionEnabled || !e?.activeLabel) return;
      setRefAreaLeft(e.activeLabel);
      setRefAreaRight(null);
      setIsSelecting(true);
    },
    [selectionEnabled]
  );

  /**
   * Mouse move: update selection end point
   */
  const handleMouseMove = useCallback(
    (e: any) => {
      if (!isSelecting || !e?.activeLabel) return;
      setRefAreaRight(e.activeLabel);
    },
    [isSelecting]
  );

  /**
   * Mouse up: complete selection and notify parent
   */
  const handleMouseUp = useCallback(() => {
    if (!isSelecting) return;

    if (refAreaLeft && refAreaRight && refAreaLeft !== refAreaRight) {
      const selection = buildSelection(refAreaLeft, refAreaRight);
      onSelectionChange?.(selection);
    } else {
      // Click without drag or same point: clear selection
      onSelectionChange?.(null);
    }

    setIsSelecting(false);
    // Keep the selection visible (don't clear refAreaLeft/Right)
  }, [
    isSelecting,
    refAreaLeft,
    refAreaRight,
    buildSelection,
    onSelectionChange,
  ]);

  /**
   * Clear selection when clicking outside the selection area
   */
  const handleChartClick = useCallback(
    (chartData: any) => {
      // If we have a selection and user clicks (not drags), check if it should clear
      if (!isSelecting && refAreaLeft && refAreaRight) {
        // Don't clear on click - user must drag to change selection
      }

      // Also call the original click handler
      if (onDataPointClick && chartData?.activePayload?.[0]) {
        const payload = chartData.activePayload[0];
        const activeIndex = chartData.activeTooltipIndex ?? 0;
        const data = chartData.data?.data ?? [];
        const normalizedEvent: ChartClickEvent = {
          dataIndex: activeIndex,
          periodLabel: (data as RechartsDataPoint[])[activeIndex]?.date ?? "",
          metricKey: payload.dataKey,
          value: payload.value,
          rawEvent: chartData,
        };
        onDataPointClick(normalizedEvent);
      }
    },
    [isSelecting, refAreaLeft, refAreaRight, onDataPointClick]
  );

  // Notify parent when chart data is ready
  useEffect(() => {
    if (chartData.data?.data && onChartDataReady) {
      onChartDataReady(chartData.data.data);
    }
  }, [chartData.data?.data, onChartDataReady]);

  // Calculate effective height (add space for brush when enabled)
  const effectiveHeight = brushEnabled ? height + brushHeight + 10 : height;

  // Get the first metric's dataKey for brush panorama preview
  const brushDataKey = series[0]?.dataKey;

  return (
    <LoadingGuard isLoading={isLoading}>
      <Box sx={{ minWidth: width, height: effectiveHeight }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData.data?.data ?? []}
            stackOffset="expand"
            margin={{
              top: 20,
              right: hasComparator ? 60 : 20,
              bottom: 20,
              left: 20,
            }}
            onClick={
              selectionEnabled
                ? handleChartClick
                : createClickHandler(
                    chartData.data?.data ?? [],
                    onDataPointClick
                  )
            }
            onMouseDown={selectionEnabled ? handleMouseDown : undefined}
            onMouseMove={selectionEnabled ? handleMouseMove : undefined}
            onMouseUp={selectionEnabled ? handleMouseUp : undefined}
            onMouseLeave={selectionEnabled ? handleMouseUp : undefined}
          >
            {/* Grid */}
            {grid && (
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={grid.vertical}
                horizontal={grid.horizontal}
                stroke={theme.palette.divider}
              />
            )}

            {/* X Axis */}
            <XAxis
              dataKey="date"
              tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
              tickLine={{ stroke: theme.palette.divider }}
              axisLine={{ stroke: theme.palette.divider }}
              onClick={(data) => {
                if (onPeriodClick && data) {
                  const normalizedEvent: ChartClickEvent = {
                    dataIndex: data.index ?? 0,
                    periodLabel: String(data.value ?? ""),
                    rawEvent: data,
                  };
                  onPeriodClick(normalizedEvent);
                }
              }}
            />

            {/* Left Y Axis (comparand) */}
            <YAxis
              yAxisId="left"
              orientation="left"
              tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
              tickLine={{ stroke: theme.palette.divider }}
              axisLine={{ stroke: theme.palette.divider }}
              tickFormatter={leftAxisFormatter}
              domain={[
                comparand.yAxisConfig?.minValue ?? "auto",
                comparand.yAxisConfig?.maxValue ?? "auto",
              ]}
              label={
                comparand.yAxisConfig?.label
                  ? {
                      value: comparand.yAxisConfig.label,
                      angle: -90,
                      position: "insideLeft",
                      fill: theme.palette.text.primary,
                      fontSize: 12,
                    }
                  : undefined
              }
            />

            {/* Right Y Axis (comparator) - only if comparator is active */}
            {hasComparator && (
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                tickLine={{ stroke: theme.palette.divider }}
                axisLine={{ stroke: theme.palette.divider }}
                tickFormatter={rightAxisFormatter}
                domain={[
                  comparator?.yAxisConfig?.minValue ?? "auto",
                  comparator?.yAxisConfig?.maxValue ?? "auto",
                ]}
                label={
                  comparator?.yAxisConfig?.label
                    ? {
                        value: comparator.yAxisConfig.label,
                        angle: 90,
                        position: "insideRight",
                        fill: theme.palette.text.primary,
                        fontSize: 12,
                      }
                    : undefined
                }
              />
            )}

            {/* Legend */}
            <Legend
              wrapperStyle={{ paddingTop: 10, fontSize: 10, maxHeight: 100 }}
              formatter={(value) => (
                <span style={{ color: theme.palette.text.primary }}>
                  {value}
                </span>
              )}
            />

            {/* All series (left and right Y axis) */}
            {renderSeries(series, aggregationMode, connectNulls)}
            <Tooltip content={tooltipRenderer} wrapperStyle={{zIndex: 9999}} />

            {/* ReferenceArea for drag-to-select highlighting */}
            {selectionEnabled && refAreaLeft && refAreaRight && (
              <ReferenceArea
                x1={refAreaLeft}
                x2={refAreaRight}
                strokeOpacity={0.3}
                stroke={theme.palette.primary.main}
                fill={theme.palette.primary.main}
                fillOpacity={isSelecting ? 0.2 : 0.3}
                yAxisId="left"
              />
            )}

            {/* Brush for time range selection */}
            {brushEnabled && (
              <Brush
                dataKey="date"
                height={brushHeight}
                stroke={theme.palette.primary.main}
                fill={theme.palette.background.paper}
                tickFormatter={(value) => value}
                startIndex={controlledBrushRange?.startIndex}
                endIndex={controlledBrushRange?.endIndex}
                onChange={handleBrushChange}
              >
                {/* Panorama preview chart inside brush */}
                <AreaChart>
                  <Area
                    type="monotone"
                    dataKey={brushDataKey ?? "date"}
                    stroke={theme.palette.primary.light}
                    fill={theme.palette.primary.light}
                    fillOpacity={0.3}
                  />
                </AreaChart>
              </Brush>
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </LoadingGuard>
  );
}
